import React from 'react';

const PositioningSection = () => {
  return (
    <section className="relative bg-white pt-8 md:pt-12 pb-24 md:pb-32 overflow-hidden">
      {/* CSS for subtle floating animation */}
      <style>{`
        @keyframes float-subtle {
          0%, 100% { transform: translateY(0px) rotate(var(--rotate, 0deg)); }
          50% { transform: translateY(-4px) rotate(var(--rotate, 0deg)); }
        }
        .tag-float {
          animation: float-subtle 3s ease-in-out infinite;
        }
        .tag-float-delay-1 { animation-delay: 0.5s; }
        .tag-float-delay-2 { animation-delay: 1s; }
        .tag-float-delay-3 { animation-delay: 1.5s; }
        .tag-float-delay-4 { animation-delay: 2s; }
        .tag-float-delay-5 { animation-delay: 2.5s; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6">
        {/* Main Container */}
        <div className="grid lg:grid-cols-2 gap-0 max-w-5xl mx-auto overflow-hidden rounded-3xl shadow-2xl">
          
          {/* LEFT SIDE - Traditional */}
          <div className="relative bg-gray-950 p-10 md:p-12 lg:p-14 min-h-[480px]">
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-black"></div>
            
            {/* Content */}
            <div className="relative z-10 h-full flex flex-col">
              {/* Header */}
              <div className="mb-8">
                <span className="inline-block px-3 py-1 text-xs font-medium text-gray-400 bg-gray-800/50 rounded-full mb-4 tracking-wide uppercase">
                  The Old Way
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Traditional
                </h3>
              </div>

              {/* Scattered Tags Container */}
              <div className="relative flex-1 min-h-[280px]">
                {/* Tag: Accounts */}
                <div className="absolute top-[5%] left-[5%] tag-float" style={{'--rotate': '-3deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Accounts
                  </span>
                </div>

                {/* Tag: Permissions */}
                <div className="absolute top-[8%] right-[10%] tag-float tag-float-delay-1" style={{'--rotate': '2deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Permissions
                  </span>
                </div>

                {/* Tag: Centralized platforms */}
                <div className="absolute top-[28%] left-[8%] tag-float tag-float-delay-2" style={{'--rotate': '1deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Centralized platforms
                  </span>
                </div>

                {/* Tag: Ads */}
                <div className="absolute top-[32%] right-[12%] tag-float tag-float-delay-3" style={{'--rotate': '-2deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Ads
                  </span>
                </div>

                {/* Tag: Monetized data */}
                <div className="absolute top-[55%] left-[12%] tag-float tag-float-delay-4" style={{'--rotate': '3deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Monetized data
                  </span>
                </div>

                {/* Tag: Downtime */}
                <div className="absolute top-[75%] right-[18%] tag-float tag-float-delay-5" style={{'--rotate': '-1deg'}}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800/60 border border-gray-700/50 rounded-full text-gray-400 text-sm font-medium backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    Downtime
                  </span>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-[45%] left-[55%] w-20 h-20 border border-gray-800 rounded-full opacity-30"></div>
                <div className="absolute top-[70%] left-[40%] w-12 h-12 border border-gray-800 rounded-full opacity-20"></div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - QS Workspaces */}
          <div className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-10 md:p-12 lg:p-14 min-h-[480px]">
            {/* Content */}
            <div className="relative z-10 h-full flex flex-col">
              {/* Header */}
              <div className="mb-8">
                <span className="inline-block px-3 py-1 text-xs font-medium text-emerald-600 bg-emerald-100 rounded-full mb-4 tracking-wide uppercase">
                  The QS Way
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                  QS Workspaces
                </h3>
              </div>

              {/* Scattered Tags Container */}
              <div className="relative flex-1 min-h-[280px]">
                {/* Tag: Anonymous by default */}
                <div className="absolute top-[2%] left-[5%] tag-float" style={{'--rotate': '2deg'}}>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-emerald-200 rounded-full text-gray-700 text-sm font-semibold shadow-lg shadow-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Anonymous by default
                  </span>
                </div>

                {/* Tag: User-controlled spaces */}
                <div className="absolute top-[18%] right-[5%] tag-float tag-float-delay-1" style={{'--rotate': '-2deg'}}>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-emerald-200 rounded-full text-gray-700 text-sm font-semibold shadow-lg shadow-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    User-controlled spaces
                  </span>
                </div>

                {/* Tag: User-owned data */}
                <div className="absolute top-[38%] left-[3%] tag-float tag-float-delay-2" style={{'--rotate': '-1deg'}}>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-emerald-200 rounded-full text-gray-700 text-sm font-semibold shadow-lg shadow-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    User-owned data
                  </span>
                </div>

                {/* Tag: Optional identity */}
                <div className="absolute top-[55%] right-[8%] tag-float tag-float-delay-3" style={{'--rotate': '1deg'}}>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-emerald-200 rounded-full text-gray-700 text-sm font-semibold shadow-lg shadow-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Optional identity
                  </span>
                </div>

                {/* Tag: Aligned incentives */}
                <div className="absolute top-[75%] left-[10%] tag-float tag-float-delay-4" style={{'--rotate': '2deg'}}>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-emerald-200 rounded-full text-gray-700 text-sm font-semibold shadow-lg shadow-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Aligned incentives
                  </span>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-[30%] right-[35%] w-20 h-20 border-2 border-emerald-100 rounded-full opacity-40"></div>
                <div className="absolute top-[65%] right-[30%] w-14 h-14 border-2 border-teal-100 rounded-full opacity-30"></div>
              </div>

              {/* Bottom tagline */}
              <div className="mt-auto pt-4">
                <p className="text-sm text-gray-500">
                  Just open, use, and go. <span className="text-emerald-600 font-semibold">That simple.</span>
                </p>
              </div>
            </div>

            {/* Decorative blur accent */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full blur-3xl opacity-40 -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-teal-200 to-emerald-200 rounded-full blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PositioningSection;
